import { useState, useEffect, useCallback, useRef } from 'react';
import { useResponsive } from './useResponsive';

/**
 * Custom hook to handle mobile viewport dynamically
 * Detects viewport height changes when keyboard appears/disappears
 */
export const useMobileViewport = () => {
  const { isMobile } = useResponsive();

  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight;
    }
    return 0;
  });

  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const initialHeightRef = useRef(null);
  const timeoutRef = useRef(null);

  const updateViewportHeight = useCallback(() => {
    if (typeof window === 'undefined' || !isMobile) return;

    const currentHeight = window.innerHeight;
    const visualViewport = window.visualViewport;

    // If visualViewport API is available (more precise)
    if (visualViewport) {
      const actualHeight = visualViewport.height;

      // Initialize reference height
      if (initialHeightRef.current === null) {
        initialHeightRef.current = actualHeight;
      }

      // Detect if keyboard is visible (significant height reduction)
      const heightDifference = initialHeightRef.current - actualHeight;
      const keyboardThreshold = 150; // px
      const newKeyboardVisible = heightDifference > keyboardThreshold;

      setViewportHeight(actualHeight);
      setIsKeyboardVisible(newKeyboardVisible);
    } else {
      // Fallback using window.innerHeight
      if (initialHeightRef.current === null) {
        initialHeightRef.current = currentHeight;
      }

      const heightDifference = initialHeightRef.current - currentHeight;
      const keyboardThreshold = 150; // px
      const newKeyboardVisible = heightDifference > keyboardThreshold;

      setViewportHeight(currentHeight);
      setIsKeyboardVisible(newKeyboardVisible);
    }
  }, [isMobile]);

  const debouncedUpdate = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(updateViewportHeight, 100);
  }, [updateViewportHeight]);

  useEffect(() => {
    if (!isMobile) {
      setViewportHeight(window.innerHeight);
      setIsKeyboardVisible(false);
      return;
    }

    // Initial update
    updateViewportHeight();

    // Listeners for viewport changes
    const handleResize = () => debouncedUpdate();
    const handleVisualViewportChange = () => debouncedUpdate();
    const handleOrientationChange = () => {
      // Reset on orientation change
      setTimeout(() => {
        initialHeightRef.current = null;
        updateViewportHeight();
      }, 500);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    // Visual Viewport API if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      }
    };
  }, [isMobile, debouncedUpdate, updateViewportHeight]);

  return {
    viewportHeight,
    isKeyboardVisible,
    isMobile,
    // Helpers for CSS styles
    mobileViewportStyle: isMobile
      ? {
          height: `${viewportHeight}px`,
          maxHeight: `${viewportHeight}px`,
        }
      : {},
  };
};

export default useMobileViewport;
